import { ArrowLeft, Bell, Building2, Check, Clock3, Send } from "lucide-react";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useSession } from "../auth/SessionContext";
import { Button, Card, DataAge, ErrorState, LoadingBlock, PageHeader, SeverityBadge } from "../components/ui";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import { useRemote } from "../hooks/useRemote";
import { apiRequest } from "../lib/api";
import { DELIVERY_LABELS, durationBetween, durationSince, formatDateTime } from "../lib/format";
import type { ProblemListItem } from "../types";

export function ProblemDetailPage() {
  const { id = "" } = useParams();
  const { csrfToken } = useSession();
  const [acknowledging, setAcknowledging] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const remote = useRemote(() => apiRequest<ProblemListItem>(`/api/v1/problems/${encodeURIComponent(id)}`), [id], 30_000);
  useDocumentTitle(remote.data?.name ?? "Problema");

  const acknowledge = async () => {
    if (!remote.data?.alertId) return;
    setAcknowledging(true);
    setActionError(null);
    try {
      await apiRequest(`/api/v1/alerts/${encodeURIComponent(remote.data.alertId)}/acknowledge`, { method: "POST", csrfToken });
      remote.reload();
    } catch (error) {
      setActionError(error instanceof Error ? error.message : "No se pudo completar la operación.");
    } finally {
      setAcknowledging(false);
    }
  };

  const summary = remote.data ? problemSummary(remote.data) : null;
  return (
    <div className="page page--narrow">
      <Link className="back-link" to="/problemas"><ArrowLeft size={16} /> Volver a problemas</Link>
      {remote.loading && !remote.data ? <LoadingBlock rows={6} /> : null}
      {remote.error && !remote.data ? <ErrorState error={remote.error} onRetry={remote.reload} /> : null}
      {remote.data && summary ? (
        <>
          <PageHeader eyebrow="Detalle del problema" title={remote.data.name} description={summary.description} actions={remote.data.alertId && !remote.data.acknowledgedAt && remote.data.status === "open" ? (
            <Button loading={acknowledging} onClick={() => void acknowledge()}><Check size={16} /> Marcar atendido</Button>
          ) : undefined} />
          {actionError ? <div className="inline-error" role="alert">{actionError}</div> : null}
          <Card className="problem-detail-hero">
            <div><SeverityBadge severity={remote.data.severity} /><h2>{summary.title}</h2><p>{summary.timing}</p></div>
            <span className={`problem-detail-hero__mark problem-detail-hero__mark--${remote.data.severity}`}><Bell size={30} /></span>
          </Card>
          <div className="detail-grid">
            <Card><span className="detail-label"><Building2 size={15} /> Edificio</span><strong>{remote.data.building?.name ?? "Monitoreo general"}</strong>{remote.data.building ? <Link to={`/edificios/${remote.data.building.id}`}>Ver edificio</Link> : null}</Card>
            <Card><span className="detail-label"><Clock3 size={15} /> Inicio</span><strong>{formatDateTime(remote.data.startedAt)}</strong><DataAge value={remote.data.startedAt} /></Card>
            <Card><span className="detail-label"><Check size={15} /> Atención</span><strong>{remote.data.acknowledgedAt ? "Marcado como atendido" : "Todavía no atendido"}</strong>{remote.data.acknowledgedAt ? <span>{formatDateTime(remote.data.acknowledgedAt)}</span> : null}</Card>
            <Card><span className="detail-label"><Send size={15} /> Avisos</span><strong>{!remote.data.notificationStatus || remote.data.notificationStatus === "not_routed" ? "Sin envío inmediato" : DELIVERY_LABELS[remote.data.notificationStatus]}</strong><span>Según la ruta de severidad</span></Card>
          </div>
        </>
      ) : null}
    </div>
  );
}

function problemSummary(problem: ProblemListItem): { title: string; description: string; timing: string } {
  if (problem.status === "recovered") {
    return {
      title: "Este problema ya se recuperó",
      description: "El incidente queda guardado en el historial general aunque ya no esté activo.",
      timing: problem.recoveredAt
        ? `Se recuperó el ${formatDateTime(problem.recoveredAt)} y duró ${durationBetween(problem.startedAt, problem.recoveredAt)}.`
        : `Comenzó el ${formatDateTime(problem.startedAt)}.`,
    };
  }
  if (problem.status === "suppressed") {
    return {
      title: "Este problema está suprimido",
      description: "La fuente de monitoreo lo marcó como suprimido y no requiere atención inmediata.",
      timing: `Comenzó el ${formatDateTime(problem.startedAt)}.`,
    };
  }
  return {
    title: "Este problema sigue activo",
    description: "El monitoreo todavía informa este incidente.",
    timing: `Activo hace ${durationSince(problem.startedAt)}.`,
  };
}
